import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
  PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OVERRIDE,
} from 'common/propertyAttributeConstants';

import getListingFormFields from 'common/listingFormFields';

import {
  SR_API_ACTION_FLOOR_PLAN_UPSERT,
  SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
} from 'api/actionTypes';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import log from 'client/lib/log';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFormikField, {
  SR_FIELD_THEME_ADMIN,
} from 'client/components/SrFormikField';
import {
  SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_FLOOR_PLAN,
} from 'client/components/SrAdminPhotosEditor';

import postWithState from 'client/actions/postWithState';

function getFloorPlanFormFields({ floorPlan }) {
  return [
    {
      id: 'apartmentId',
      type: 'hidden',
    },
    {
      id: 'displayName',
      type: 'short_text',
      title: 'display name',
      validations: ['required'],
    },
    ...getListingFormFields({
      fieldgroup: [
        'bedrooms',
        'bathrooms',
      ],
      updates: {
        bedrooms: {
          validations: ['required'],
        },
        bathrooms: {
          validations: ['required'],
        },
      },
    }),
    {
      id: 'description',
      type: 'long_text',
      title: 'description',
    },
    {
      id: 'tourableApartmentUnits',
      type: floorPlan.id ? 'long_text' : 'hidden',
      title: 'show units',
      properties: {
        placeholder: '["204", "205"]',
      },
      validations: floorPlan.id ? ['stringifiedArray'] : [],
    },
    {
      id: 'coverPhotoUrl',
      title: 'cover photo',
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: '/floor-plan-uploads/cover-photos/{uuid}',
      },
      validations: ['required'],
    },
    {
      id: 'photoUrls',
      title: 'photos',
      type: 'file_multi',
      properties: {
        filePath: SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_FLOOR_PLAN,
        fileAccept: 'image/*',
      },
    },
  ];
}

class SrFloorPlanModal extends Component {
  static propTypes = {
    floorPlan: PropTypes.object.isRequired,
    propertyAttributes: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  _onSubmitForm = (formValues) => {
    const { floorPlan, propertyAttributes, onChange, dispatch } = this.props;
    const payload = {
      id: floorPlan.id,
      ...setNullEmptyValues(formValues),
    };
    payload.tourableApartmentUnits = payload.tourableApartmentUnits
      ? JSON.parse(payload.tourableApartmentUnits).map((tourableUnit) => `${tourableUnit}`)
      : [];

    delete payload.bedrooms;
    delete payload.bathrooms;

    dispatch(postWithState, {
      action: SR_API_ACTION_FLOOR_PLAN_UPSERT,
      payload: payload,
      onSuccess: (newFloorPlan) => {
        const propertyAttributesIdsByName = propertyAttributes.reduce((memo, propertyAttribute) => ({
          ...memo,
          [propertyAttribute.name]: propertyAttribute.id,
        }), {});

        dispatch(postWithState, {
          action: SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
          payload: {
            propertyAttributes: [
              {
                id: propertyAttributesIdsByName.bedrooms,
                apartmentId: newFloorPlan.apartmentId,
                apartmentBuildingId: null,
                floorPlanId: newFloorPlan.id,
                category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
                cascadingBehavior: PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OVERRIDE,
                name: 'bedrooms',
                attrs: {
                  value: formValues.bedrooms,
                },
              },
              {
                id: propertyAttributesIdsByName.bathrooms,
                apartmentId: newFloorPlan.apartmentId,
                apartmentBuildingId: null,
                floorPlanId: newFloorPlan.id,
                category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
                cascadingBehavior: PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OVERRIDE,
                name: 'bathrooms',
                attrs: {
                  value: formValues.bathrooms,
                },
              },
            ],
          },
          onSuccess: onChange,
        });
      },
      onError: (error) => {
        log.error(error);
        global.alert(error.message);
      },
    });
  }

  render() {
    const { floorPlan, propertyAttributes, onClose } = this.props;
    const floorPlanFormFields = getFloorPlanFormFields({ floorPlan });
    const propertyAttributesValuesByName = propertyAttributes.reduce((memo, propertyAttribute) => ({
      ...memo,
      [propertyAttribute.name]: propertyAttribute.attrs.value,
    }), {});

    return (
      <SrDarkModal onClose={onClose}>
        <SrModalContent style={{ maxWidth: 440, height: '94vh', marginTop: 4 }}>
          <SrFlex justify='center'>
            <SrText size='2xl' align='center' font='whitney-sc'>
              floor plan
            </SrText>
          </SrFlex>
          <SrBox marginTop={20} overflowX='hidden'>
            <Formik
              initialValues={createInitialValues({
                formFields: floorPlanFormFields,
                defaultValues: {
                  ...floorPlan,
                  tourableApartmentUnits: Array.isArray(floorPlan.tourableApartmentUnits)
                    && floorPlan.tourableApartmentUnits.length
                    ? JSON.stringify(floorPlan.tourableApartmentUnits)
                    : null,
                  photoUrls: floorPlan.photoUrls || [],
                  bedrooms: propertyAttributesValuesByName.bedrooms,
                  bathrooms: propertyAttributesValuesByName.bathrooms,
                },
              })}
              validate={createFormValidationFn(floorPlanFormFields)}
              onSubmit={this._onSubmitForm}
              render={(formikBag) => {
                return (
                  <Form autoComplete='off'>
                    {floorPlanFormFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        className={SR_FIELD_THEME_ADMIN}
                      />
                    ))}
                    <SrBox marginTop={20}>
                      <ButtonDeprecated
                        type='submit'
                      >
                        {floorPlan.id ? 'update' : 'create'}
                      </ButtonDeprecated>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrFloorPlanModal.displayName = 'SrFloorPlanModal';

export default SrFloorPlanModal;
